import React, { useMemo, useState } from 'react';
import { Row, Col, Button, Select, Alert, Empty, Table, Tabs, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ChartCard from '@/components/ChartCard';
import { useMonthlyClearing } from '@/api/monthlyClearing';
import type { MonthlyClearingRow, MonthlyAdjustment } from '@/api/monthlyClearing';
import { format2 } from '@/utils/charts';
import type { EChartsOption } from 'echarts';

const MonthlyClearingTab: React.FC = () => {
  const [selectedStation, setSelectedStation] = useState<string | undefined>(undefined);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const queryMonths = selectedMonths.length > 0 ? selectedMonths : undefined;

  const { data, isLoading, error, refetch } = useMonthlyClearing({
    station_name: selectedStation,
    months: queryMonths,
  });

  const rows: MonthlyClearingRow[] = data?.data?.rows ?? [];
  const rawAdjustments: MonthlyAdjustment[] = data?.data?.adjustments ?? [];
  const availableMonths: string[] = data?.data?.available_months ?? [];
  const availableStations: string[] = data?.data?.available_stations ?? [];

  // Filter adjustments to match station / month selection
  const adjustments = useMemo(() => {
    return rawAdjustments.filter((a) => {
      if (selectedStation && a.station_name !== selectedStation) return false;
      if (queryMonths && !queryMonths.includes(a.clearing_month)) return false;
      return true;
    });
  }, [rawAdjustments, selectedStation, queryMonths]);

  // ── Extract metric columns from rows ──
  const metricCols = useMemo(() => {
    if (!rows.length) return [];
    const fixedKeys = new Set(['station_name', 'clearing_month', 'date', 'unit_name']);
    return Object.keys(rows[0]).filter((k) => !fixedKeys.has(k)).sort();
  }, [rows]);

  // ── Table columns ──
  const tableColumns = useMemo(() => {
    const baseCols = [
      { title: '日期', dataIndex: 'date', key: 'date', width: 110, fixed: 'left' as const },
      { title: '结算月份', dataIndex: 'clearing_month', key: 'clearing_month', width: 100 },
      { title: '场站', dataIndex: 'station_name', key: 'station_name', width: 140 },
    ];
    const metricColumns = metricCols.map((k) => ({
      title: k.replace(/_/g, '-'),
      dataIndex: k,
      key: k,
      align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : v ?? '-'),
      width: 140,
      ellipsis: true,
    }));
    return [...baseCols, ...metricColumns];
  }, [metricCols]);

  const adjColumns = [
    { title: '场站', dataIndex: 'station_name', key: 'station_name', width: 120 },
    { title: '结算月份', dataIndex: 'clearing_month', key: 'clearing_month', width: 100 },
    { title: '追退补月度', dataIndex: 'adjust_month', key: 'adjust_month', width: 100 },
    { title: '结算科目', dataIndex: 'adjust_item', key: 'adjust_item', width: 160 },
    { title: '调整前电量', dataIndex: 'pre_adjust_energy', key: 'pre_adjust_energy', align: 'right' as const, render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '调整前电费', dataIndex: 'pre_adjust_fee', key: 'pre_adjust_fee', align: 'right' as const, render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
  ];

  // ── Chart: daily fee per day ──
  const chartOption: EChartsOption = useMemo(() => {
    if (!rows.length) return {};
    const feeCol = metricCols.find((k) => k.includes('总结算费用_总结算电费'));
    if (!feeCol) return {};

    const dateMap = new Map<string, number>();
    rows.forEach((r) => {
      const d = String(r.date ?? '');
      const v = typeof r[feeCol] === 'number' ? r[feeCol] : 0;
      dateMap.set(d, (dateMap.get(d) ?? 0) + (v as number));
    });
    const sortedDates = Array.from(dateMap.keys()).sort();
    const feeValues = sortedDates.map((d) => dateMap.get(d) ?? 0);

    return {
      tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => typeof v === 'number' ? format2(v) : String(v) },
      legend: { bottom: 30 },
      grid: { left: 50, right: 20, top: 20, bottom: 80 },
      xAxis: { type: 'category', data: sortedDates, axisLabel: { rotate: 30, fontSize: 11 } },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#9ca3af' },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      series: [
        {
          name: '日总结算电费',
          type: 'bar',
          data: feeValues,
          itemStyle: { color: '#0d9488' },
        },
      ],
      dataZoom: [{ type: 'slider', start: 0, end: 100, bottom: 40 }],
    };
  }, [rows, metricCols]);

  // ── KPI cards ──
  const kpiLabel = queryMonths ? queryMonths.join(' / ') : '全部月份';

  const kpis = useMemo(() => {
    if (!rows.length) return null;
    const feeCol = metricCols.find((k) => k.includes('总结算费用_总结算电费'));
    const energyCol = metricCols.find((k) => k.includes('总结算费用_总结算电量'));
    let totalFee = 0;
    let totalEnergy = 0;
    rows.forEach((r) => {
      if (feeCol && typeof r[feeCol] === 'number') totalFee += r[feeCol] as number;
      if (energyCol && typeof r[energyCol] === 'number') totalEnergy += r[energyCol] as number;
    });
    return {
      totalFee,
      totalEnergy,
      avgPrice: totalEnergy > 0 ? totalFee / totalEnergy : 0,
      days: new Set(rows.map((r) => String(r.date ?? ''))).size,
      months: new Set(rows.map((r) => String(r.clearing_month ?? ''))).size,
    };
  }, [rows, metricCols]);

  const tabItems = [
    {
      key: 'view',
      label: '结算数据',
      children: (
        <div>
          {/* Filter bar */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={4}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>场站</div>
                <Select
                  allowClear
                  value={selectedStation}
                  options={availableStations.map((s) => ({ value: s, label: s }))}
                  onChange={(v) => setSelectedStation(v)}
                  style={{ width: '100%' }}
                  placeholder="全部场站"
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>结算月份</div>
                <Select
                  mode="multiple"
                  value={selectedMonths}
                  options={availableMonths.map((m) => ({ value: m, label: m }))}
                  onChange={(v) => setSelectedMonths(v)}
                  style={{ width: '100%' }}
                  placeholder="全部月份"
                />
              </Col>
              <Col xs={24} sm={4} md={3}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
                <Button type="primary" icon={<SearchOutlined />} onClick={() => refetch()} style={{ width: '100%' }}>
                  查询
                </Button>
              </Col>
            </Row>
          </div>

          {error && (
            <Alert message={error instanceof Error ? error.message : '请求失败'} type="error" showIcon style={{ marginBottom: 16 }} />
          )}

          {/* KPIs */}
          {kpis && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {[
                { label: `${kpiLabel} 总结算电费`, value: `${format2(kpis.totalFee)} 元` },
                { label: `${kpiLabel} 总上网电量`, value: `${format2(kpis.totalEnergy)} MWh` },
                { label: `${kpiLabel} 均价`, value: `${format2(kpis.avgPrice)} 元/MWh` },
                { label: `有效天数 / 月份数`, value: `${kpis.days}天 / ${kpis.months}月` },
              ].map((kpi, i) => (
                <Col xs={12} sm={6} key={i}>
                  <div style={{ background: '#fff', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{kpi.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>{kpi.value}</div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {/* Empty state */}
          {!isLoading && !error && rows.length === 0 && (
            <Empty description="暂无月清分数据，请前往「场站数据上传 → 月清分上传」导入结算单" style={{ marginTop: 48 }} />
          )}

          {/* Main table */}
          {rows.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 12 }}>
                月清分明细
                {queryMonths && <Tag color="blue" style={{ marginLeft: 8 }}>{queryMonths.join(', ')}</Tag>}
                {selectedStation && <Tag color="green">{selectedStation}</Tag>}
              </div>
              <Table
                columns={tableColumns}
                dataSource={rows.map((r, i) => ({ ...r, key: i }))}
                loading={isLoading}
                size="small"
                scroll={{ x: Math.max(600, metricCols.length * 140 + 350) }}
                pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
              />
            </div>
          )}

          {/* Chart */}
          {rows.length > 0 && (
            <ChartCard title={`日结算电费趋势 · ${kpiLabel}`} option={chartOption} loading={isLoading} error={null} height={350} />
          )}
        </div>
      ),
    },
    {
      key: 'adjustments',
      label: `追退补明细${adjustments.length > 0 ? ` (${adjustments.length})` : ''}`,
      children: (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16 }}>
          {adjustments.length === 0 ? (
            <Empty description={queryMonths || selectedStation ? '当前筛选条件下暂无追退补记录' : '暂无追退补记录'} />
          ) : (
            <Table
              columns={adjColumns}
              dataSource={adjustments.map((r, i) => ({ ...r, key: i }))}
              size="small"
              pagination={{ pageSize: 30 }}
              scroll={{ x: 680 }}
            />
          )}
        </div>
      ),
    },
  ];

  const [subTab, setSubTab] = useState('view');
  return <Tabs activeKey={subTab} onChange={setSubTab} items={tabItems} />;
};

export default MonthlyClearingTab;
